/**
 * Every product rule of the web create-pool flow, in one pure module.
 *
 * This is the web twin of `apps/mobile/src/pools/createPoolForm.ts`
 * (ISSUE-046). `apps/app` cannot import from `apps/mobile`, so the shapes and
 * validation rules are re-declared here rather than shared.
 *
 * This file deliberately sends `autoApprove` and never the join-policy field
 * to `/pool/create`. They are the same setting:
 * `services/pool-api/src/api/private/handlers/pool/postPoolCreateHandler/index.ts:91-99`
 * rejects a body that sends both with disagreeing values.
 *
 * It sends no season either (ISSUE-1092): pool-api asks sports-api which
 * season a pool in the league is for, and the review step names it from the
 * league catalog's poolSeasonLabel.
 */

import {
  allowsCustomLeague,
  CUSTOM_LEAGUE_KEY,
  CUSTOM_LEAGUE_LABEL,
  CUSTOM_SPORT_KEY,
  isCustomLeagueKey,
  type typeContestRules,
} from "@sportsmet/contest-engine";

import {
  EMPTY_POOL_TYPE_SETUPS,
  isSetupSlotPoolType,
  POOL_TYPE_CHAT_DEFAULTS,
  poolTypeCreateFields,
  type typePoolTypeCreateFields,
  type typePoolTypeSetups,
} from "./poolTypeSetups";
import {
  EMPTY_SURVIVOR_RULES_DRAFT,
  emptySurvivorRulesDraft,
  rulesDraftFromContestConfig,
  type typeSurvivorRulesDraft,
} from "./survivorRulesDraft";

export type typeCatalogLeague = {
  leagueKey: string;
  name: string;
  shortName: string;
  isPoolEligible: boolean;
  /**
   * ISSUE-1092. The season a new pool in this league is for, resolved by
   * sports-api. Optional only so the pools screen's own catalog fixtures,
   * which never read it, need not carry it.
   */
  poolSeasonLabel?: string;
};

export type typeCatalogSport = {
  sportKey: string;
  name: string;
  leagues: readonly typeCatalogLeague[];
};

export type typeCreatePoolVisibility = "private" | "public";

export type typeCreatePoolDraft = {
  sportKey: string | null;
  leagueKey: string | null;
  contestType: string | null;
  name: string;
  autoApprove: boolean;
  memberSharingEnabled: boolean;
  chatEnabled: boolean;
  visibility: typeCreatePoolVisibility;
  quickStartId: string | null;
  rules: typeSurvivorRulesDraft;
  /**
   * ISSUE-1258. Each non-Survivor pool type's own setup draft, filled by its
   * setup step (`poolTypeSetups.ts`). Survivor's rules stay in `rules`.
   */
  typeSetups: typePoolTypeSetups;
  /**
   * ISSUE-1098. The contest rules the chosen Quick start wrote into `rules`,
   * or null when no Quick start is chosen or the chosen one has none. Lets a
   * switch away undo exactly what the template set and nothing the fan set.
   */
  appliedQuickStartRules: typeContestRules | null;
  /** ISSUE-201. Set once /upload/complete answers published; attached to the pool right after /pool/create returns a poolId. */
  bannerMediaId: string | null;
};

export type typeCreatePoolLeagueOption = {
  sportKey: string;
  leagueKey: string;
  /** The league's full name, e.g. "National Football League". */
  label: string;
  /** The sport's name. */
  description: string;
  /** The league's short name, e.g. "NFL" (ISSUE-1092 tile title). */
  shortName: string;
  /** The season a new pool is for, e.g. "2026-27" (ISSUE-1092). */
  seasonLabel: string;
};

export type typePoolTypeRow = {
  contestType: string;
  label: string;
  description: string;
  isAvailable: boolean;
};

export type typeQuickStartOption = {
  quickStartId: string;
  title: string;
  summary: string;
  config: {
    sportKey: string;
    leagueKey: string;
    contestType: string;
    joinPolicy: "open" | "approval";
    memberSharingEnabled: boolean;
    /** ISSUE-1098. Rules staff copied from a pool; null or absent when none. */
    contestSettings?: typeContestRules | null;
  };
};

export type typeCreatePoolContestType =
  "survivor" | "squares" | "stripCard" | "pickem";

export type typeCreatePoolBody = typePoolTypeCreateFields & {
  name: string;
  sportKey: string;
  leagueKey: string;
  contestType: typeCreatePoolContestType;
  autoApprove: boolean;
  memberSharingEnabled: boolean;
  chatEnabled: boolean;
  visibility: typeCreatePoolVisibility;
  maxMembers: number | null;
  quickStartId?: string;
};

/** Matches `services/pool-api/src/models/pools.ts:18-23` field for field. */
export const EMPTY_CREATE_POOL_DRAFT: typeCreatePoolDraft = {
  sportKey: null,
  leagueKey: null,
  contestType: null,
  name: "",
  autoApprove: false,
  memberSharingEnabled: true,
  chatEnabled: true,
  visibility: "private",
  quickStartId: null,
  rules: EMPTY_SURVIVOR_RULES_DRAFT,
  typeSetups: EMPTY_POOL_TYPE_SETUPS,
  appliedQuickStartRules: null,
  bannerMediaId: null,
};

/** The server's real cap, `postPoolCreateHandler/index.ts:48`. */
export const POOL_NAME_MAX_LENGTH = 80;

export const POOL_NAME_REQUIRED_ERROR = "Enter a name for your pool.";

/**
 * What an unavailable pool type's tile says. ISSUE-1258 retired the client's
 * own parked list: `/pool/create-options` says per type whether it is
 * available, and the tile follows.
 */
export const PARKED_POOL_TYPE_DESCRIPTION = "Coming soon";
/** ISSUE-1281. A type that does not run in the league picked (Squares in the WNBA). */
export const NOT_IN_LEAGUE_POOL_TYPE_DESCRIPTION =
  "Not offered for this league yet";

/** ISSUE-1299. What a pool type's tile says when it cannot run with no league. */
export const NEEDS_LEAGUE_POOL_TYPE_DESCRIPTION = "Needs a league";

/**
 * ISSUE-1299. The Custom tile on the league step: a pool with no league,
 * after v1's Custom league. Its games are custom games the Commissioner
 * names, so there is no season to show.
 */
export const CUSTOM_LEAGUE_OPTION: typeCreatePoolLeagueOption = {
  sportKey: CUSTOM_SPORT_KEY,
  leagueKey: CUSTOM_LEAGUE_KEY,
  label: "No league, you name the games",
  description: CUSTOM_LEAGUE_LABEL,
  shortName: CUSTOM_LEAGUE_LABEL,
  seasonLabel: "",
};

/**
 * ISSUE-1299. The league tiles plus the Custom tile, which shows only when
 * `/pool/create-options` says at least one pool type can run with no league.
 */
export function withCustomLeagueOption(
  options: readonly typeCreatePoolLeagueOption[],
  serverTypes: readonly { allowsCustomLeague?: boolean }[],
): readonly typeCreatePoolLeagueOption[] {
  const isOffered = serverTypes.some(
    (type) => type.allowsCustomLeague === true,
  );
  return isOffered ? [...options, CUSTOM_LEAGUE_OPTION] : options;
}

/**
 * ISSUE-1258. Picking a pool type also sets the chat switch to that type's
 * default (MSG-3: Strip Card starts off, every other type on).
 */
export function chooseContestType(
  draft: typeCreatePoolDraft,
  contestType: string,
): typeCreatePoolDraft {
  return {
    ...draft,
    contestType,
    chatEnabled:
      POOL_TYPE_CHAT_DEFAULTS[contestType] ??
      EMPTY_CREATE_POOL_DRAFT.chatEnabled,
  };
}

export function poolEligibleLeagueOptions(
  sports: readonly typeCatalogSport[] | null,
): readonly typeCreatePoolLeagueOption[] {
  if (sports === null) return [];

  const options: typeCreatePoolLeagueOption[] = [];
  for (const sport of sports) {
    for (const league of sport.leagues) {
      if (!league.isPoolEligible) continue;
      options.push({
        sportKey: sport.sportKey,
        leagueKey: league.leagueKey,
        label: league.name,
        description: sport.name,
        shortName: league.shortName,
        seasonLabel: league.poolSeasonLabel ?? "",
      });
    }
  }
  return options;
}

/**
 * Changing the league drops any Quick start already chosen.
 *
 * `/quick-start/list` is scoped by `leagueKey`, so a template picked for the
 * old league is not on offer for the new one. `postPoolCreateHandler` checks
 * only that the id is visible and not removed, never that its league matches
 * the body, so a stale id would create a pool in league B carrying league A's
 * join policy and member sharing, and would record `createdFromQuickStartId`
 * pointing at a template the commissioner was never shown.
 */
export function chooseLeague(
  draft: typeCreatePoolDraft,
  option: typeCreatePoolLeagueOption,
): typeCreatePoolDraft {
  const isCustomLeague = isCustomLeagueKey(option.leagueKey);
  const withLeague: typeCreatePoolDraft = {
    ...draft,
    sportKey: option.sportKey,
    leagueKey: option.leagueKey,
    // ISSUE-1299. The Strip Card setup step asks for a game name instead of
    // a schedule game when the pool has no league.
    typeSetups: {
      ...draft.typeSetups,
      stripCard: { ...draft.typeSetups.stripCard, isCustomLeague },
    },
  };
  // ISSUE-1299. Custom runs only the types that need no league (Strip
  // Card), so a type that cannot run there gives way to Strip Card.
  const next =
    isCustomLeague &&
    (draft.contestType === null || !allowsCustomLeague(draft.contestType))
      ? chooseContestType(withLeague, "stripCard")
      : withLeague;
  // Only when a template was actually applied. `clearQuickStart` also returns
  // autoApprove and memberSharingEnabled to their defaults, and those are
  // fan-editable checkboxes on the basics step, so clearing unconditionally
  // would throw away the fan's own answers on every league change.
  return draft.quickStartId === null ? next : clearQuickStart(next);
}

/**
 * ISSUE-1098. The rules step prefilled from a Quick start's copied rules. The
 * fan's own dates and member cap stay: a Quick start carries rules only, and
 * the source pool's season dates mean nothing for a new pool.
 */
function rulesFromQuickStart(
  current: typeSurvivorRulesDraft,
  settings: typeContestRules,
): typeSurvivorRulesDraft {
  return {
    ...rulesDraftFromContestConfig({
      ...settings,
      startDate: current.startDate,
      endDate: current.endDate,
      endDateMode: current.endDateMode,
    }),
    maxMembers: current.maxMembers,
  };
}

/** ISSUE-1098. The rules step back at its defaults, keeping the fan's dates and member cap. */
function rulesWithoutQuickStart(
  current: typeSurvivorRulesDraft,
): typeSurvivorRulesDraft {
  return {
    ...EMPTY_SURVIVOR_RULES_DRAFT,
    startDate: current.startDate,
    endDate: current.endDate,
    endDateMode: current.endDateMode,
    maxMembers: current.maxMembers,
  };
}

/**
 * Applies a staff-authored Quick start to the draft. joinPolicy becomes
 * autoApprove because this file never sends a join-policy key; see the
 * header.
 *
 * ISSUE-1098. A Quick start with copied rules fills every rule on the rules
 * step, and the fan can still change any of them; the server validates them
 * again on create. One with no rules (every Quick start from before
 * ISSUE-1098) leaves the rules exactly as they were. Switching from a
 * template that wrote rules first returns them to the defaults, so one
 * template's rules never leak into another.
 */
export function chooseQuickStart(
  draft: typeCreatePoolDraft,
  option: typeQuickStartOption,
): typeCreatePoolDraft {
  const base: typeCreatePoolDraft =
    draft.appliedQuickStartRules !== null
      ? {
          ...draft,
          rules: rulesWithoutQuickStart(draft.rules),
          appliedQuickStartRules: null,
        }
      : draft;
  const settings = option.config.contestSettings ?? null;
  return {
    ...base,
    quickStartId: option.quickStartId,
    sportKey: option.config.sportKey,
    leagueKey: option.config.leagueKey,
    contestType: option.config.contestType,
    autoApprove: option.config.joinPolicy === "open",
    memberSharingEnabled: option.config.memberSharingEnabled,
    ...(settings !== null
      ? {
          rules: rulesFromQuickStart(base.rules, settings),
          appliedQuickStartRules: settings,
        }
      : { appliedQuickStartRules: null }),
  };
}

/**
 * Returns the draft to the set-it-up-myself starting point: no template,
 * and the two template-applied settings back at their defaults. The league
 * step's own answers (sport, league, contest type) are kept,
 * because the fan chose those before this step.
 *
 * ISSUE-1098. Rules a template wrote go back to the defaults (dates and member
 * cap kept). When the template wrote no rules, the rules are the fan's own and
 * stay untouched.
 */
export function clearQuickStart(
  draft: typeCreatePoolDraft,
): typeCreatePoolDraft {
  return {
    ...draft,
    quickStartId: null,
    autoApprove: EMPTY_CREATE_POOL_DRAFT.autoApprove,
    memberSharingEnabled: EMPTY_CREATE_POOL_DRAFT.memberSharingEnabled,
    ...(draft.appliedQuickStartRules !== null
      ? { rules: rulesWithoutQuickStart(draft.rules) }
      : {}),
    appliedQuickStartRules: null,
  };
}

export function applyCreatePoolDefaults(
  draft: typeCreatePoolDraft,
  options: {
    contestTypes: readonly { contestType: string }[];
  } | null,
  now: Date = new Date(),
): typeCreatePoolDraft {
  if (options === null) return draft;

  const next = { ...draft };
  if (draft.contestType === null && options.contestTypes.length === 1) {
    const [only] = options.contestTypes;
    if (only !== undefined) next.contestType = only.contestType;
  }
  if (draft.rules.startDate === "") {
    next.rules = emptySurvivorRulesDraft(now);
  }
  return next;
}

export function buildPoolTypeRows(
  serverTypes: readonly {
    contestType: string;
    name: string;
    description: string;
    /** Absent reads as available, the way every type was before ISSUE-1258. */
    isAvailable?: boolean;
    /** The only leagues the type runs in; null or absent means every league. */
    leagueKeys?: readonly string[] | null;
    /** ISSUE-1299. True when the type can run with no league (Custom). */
    allowsCustomLeague?: boolean;
  }[],
  /** The league already picked, if any (ISSUE-1281). */
  leagueKey: string | null = null,
): typePoolTypeRow[] {
  const isCustomLeague = isCustomLeagueKey(leagueKey);
  return serverTypes.map((serverType) => {
    const isOfferedHere = isCustomLeague
      ? serverType.allowsCustomLeague === true
      : leagueKey === null ||
        serverType.leagueKeys === null ||
        serverType.leagueKeys === undefined ||
        serverType.leagueKeys.includes(leagueKey);
    const isAvailable = (serverType.isAvailable ?? true) && isOfferedHere;
    return {
      contestType: serverType.contestType,
      label: serverType.name,
      description: isAvailable
        ? serverType.description
        : isOfferedHere
          ? PARKED_POOL_TYPE_DESCRIPTION
          : isCustomLeague
            ? NEEDS_LEAGUE_POOL_TYPE_DESCRIPTION
            : NOT_IN_LEAGUE_POOL_TYPE_DESCRIPTION,
      isAvailable,
    };
  });
}

/**
 * The frame's toggle reads "Approve new members myself". It is CHECKED when
 * the commissioner approves people, which is when `autoApprove === false`.
 */
export function approveMyselfChecked(draft: typeCreatePoolDraft): boolean {
  return !draft.autoApprove;
}

export function setApproveMyself(
  draft: typeCreatePoolDraft,
  checked: boolean,
): typeCreatePoolDraft {
  return { ...draft, autoApprove: !checked };
}

export function isPoolNameValid(draft: typeCreatePoolDraft): boolean {
  return draft.name.trim().length > 0;
}

export function missingCreatePoolBasicsItems(
  draft: typeCreatePoolDraft,
): readonly string[] {
  return isPoolNameValid(draft) ? [] : ["a pool name"];
}

/**
 * Compares against a baseline rather than `EMPTY_CREATE_POOL_DRAFT`, because
 * `applyCreatePoolDefaults` writes `contestType` from the server, and treating those as fan edits would raise the discard guard on a
 * flow nobody touched.
 */
export function isCreatePoolDraftDirty(
  draft: typeCreatePoolDraft,
  baseline: typeCreatePoolDraft,
): boolean {
  return (
    draft.sportKey !== baseline.sportKey ||
    draft.leagueKey !== baseline.leagueKey ||
    draft.contestType !== baseline.contestType ||
    draft.name !== baseline.name ||
    draft.autoApprove !== baseline.autoApprove ||
    draft.memberSharingEnabled !== baseline.memberSharingEnabled ||
    draft.chatEnabled !== baseline.chatEnabled ||
    draft.visibility !== baseline.visibility ||
    draft.quickStartId !== baseline.quickStartId ||
    JSON.stringify(draft.rules) !== JSON.stringify(baseline.rules) ||
    JSON.stringify(draft.typeSetups) !== JSON.stringify(baseline.typeSetups)
  );
}

/**
 * Never emits a join-policy key. Sending both the join-policy field and
 * `autoApprove` with disagreeing values makes `postPoolCreateHandler` return
 * `VALIDATION_ERROR` / `autoApproveConflict`. An "open" join policy is the
 * same fact as `autoApprove === true`, so one field carries it.
 */
export function buildCreatePoolBody(
  draft: typeCreatePoolDraft,
): typeCreatePoolBody | null {
  if (draft.sportKey === null) return null;
  if (draft.leagueKey === null) return null;
  if (!isPoolNameValid(draft)) return null;

  // ISSUE-1258. Survivor sends its rules after create through
  // /contest/update; every other type sends its setup inside the create.
  let contestType: typeCreatePoolContestType;
  let typeFields: typePoolTypeCreateFields = {};
  if (draft.contestType === "survivor") {
    contestType = "survivor";
  } else if (isSetupSlotPoolType(draft.contestType)) {
    const fields = poolTypeCreateFields(draft.contestType, draft.typeSetups);
    if (fields === null) return null;
    contestType = draft.contestType;
    typeFields = fields;
  } else {
    return null;
  }

  return {
    ...typeFields,
    name: draft.name.trim(),
    sportKey: draft.sportKey,
    leagueKey: draft.leagueKey,
    contestType,
    autoApprove: draft.autoApprove,
    memberSharingEnabled: draft.memberSharingEnabled,
    chatEnabled: draft.chatEnabled,
    visibility: draft.visibility,
    maxMembers: draft.rules.maxMembers,
    ...(draft.quickStartId !== null
      ? { quickStartId: draft.quickStartId }
      : {}),
  };
}
